// [Figma | Component Library - Bottom Sheet](https://www.figma.com/design/MmU4oUhnmciD7YZOVtAxLk/Flipster-UI-Component-Library-%2222?node-id=7024-5761)

import { useAnimate } from 'motion/react';
import { JSX, MouseEventHandler, ReactNode, Ref, createContext, useContext, useImperativeHandle } from 'react';

import { Styles, css, cx, sva } from '@flipster/styled-system/css';

import { button } from './button';

const DialogContext = createContext<{ slots?: ReturnType<typeof recipe.raw> }>({});

export interface DialogRef {
  open: () => void;
  close: () => void;
}

export interface DialogProps {
  ref?: Ref<DialogRef>;
  css?: Styles;
  children?: ReactNode;
  behavior?: 'popup' | 'bottomSheet';
  closeOnClickBackdrop?: boolean;
}

export const Dialog = ({
  ref,
  css: cssProp,
  children,
  behavior = 'popup',
  closeOnClickBackdrop = true,
}: DialogProps) => {
  const [dialogRef, animate] = useAnimate<HTMLDialogElement>();
  const slots = recipe.raw({ behavior });

  const handleOpen = () => {
    const dialogEl = dialogRef.current;
    if (!dialogEl || dialogEl.open) return;

    dialogEl.showModal();
    animate(dialogEl, { '--progress': 1 }, { ease: 'easeOut', duration: 0.2 });
  };

  const handleClose = async () => {
    const dialogEl = dialogRef.current;
    if (!dialogEl || !dialogEl.open) return;

    await animate(dialogEl, { '--progress': 0 }, { ease: 'easeIn', duration: 0.2 });
    dialogEl.close();
  };

  const handleClick: MouseEventHandler<HTMLDialogElement> = (event) => {
    event.preventDefault();
    const dialogEl = dialogRef.current;
    if (!dialogEl) return;
    if (event.target === dialogEl) handleClose();
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      onClick={closeOnClickBackdrop ? handleClick : undefined}
      className={css(slots.dialog, cssProp)}
    >
      <DialogContext value={{ slots }}>{children}</DialogContext>
    </dialog>
  );
};

export const DialogTopBar = (props: JSX.IntrinsicElements['div'] & { css?: Styles }) => {
  const { slots } = useContext(DialogContext);
  return <div {...props} className={cx(css(slots?.topBar, props.css), props.className)} />;
};

export const DialogContents = (props: JSX.IntrinsicElements['div'] & { css?: Styles }) => {
  const { slots } = useContext(DialogContext);
  return <div {...props} className={cx(css(slots?.contents, props.css), props.className)} />;
};

export const DialogBottom = (props: JSX.IntrinsicElements['div'] & { css?: Styles }) => {
  const { slots } = useContext(DialogContext);
  return <div {...props} className={cx(css(slots?.bottom, props.css), props.className)} />;
};

export const DialogTitle = (props: JSX.IntrinsicElements['h2'] & { css?: Styles }) => {
  const { slots } = useContext(DialogContext);
  return <h2 {...props} className={cx(css(slots?.title, props.css), props.className)} />;
};

export const DialogDescription = (props: JSX.IntrinsicElements['p'] & { css?: Styles }) => {
  const { slots } = useContext(DialogContext);
  return <p {...props} className={cx(css(slots?.description, props.css), props.className)} />;
};

export const DialogButton = (props: JSX.IntrinsicElements['button'] & { css?: Styles }) => {
  const { slots } = useContext(DialogContext);
  return <button {...props} className={cx(css(slots?.button, props.css), props.className)} />;
};

const recipe = sva({
  slots: ['dialog', 'topBar', 'contents', 'bottom', 'title', 'description', 'button'],
  base: {
    dialog: {
      '--progress': 0,
      position: 'fixed',
      all: 'unset',
      boxSizing: 'border-box',
      bg: 'background.default.level-1',
      _backdrop: {
        bg: 'rgba(0, 0, 0, calc(var(--progress) * 0.8))',
      },
      '&:not([open])': {
        display: 'none',
      },
    },
    topBar: {},
    contents: {},
    bottom: {},
    title: {},
    description: {},
    button: {},
  },
  variants: {
    behavior: {
      popup: {
        dialog: {
          left: '50%',
          top: '50%',
          width: 'calc(100vw - 32px)',
          maxWidth: 480,
          maxHeight: 'calc(100vh - 60px)',
          rounded: 8,
          opacity: 'calc(0.5 + var(--progress) * 0.5)',
          transform: 'translateX(-50%) translateY(-50%) scale(calc(0.8 + var(--progress) * 0.2))',
        },
        contents: {
          px: [16, 32],
          pt: [24, 32],
          pb: [16, 16],
        },
        bottom: { pt: [16, 24], pb: [16, 32], px: [16, 32], display: 'flex', gap: 8 },
        title: {
          textStyle: ['Title/XS', 'Title/S'],
          color: 'content.default.level-5',
          mb: 16,
        },
        description: {
          textStyle: ['Text/M/Regular', 'Text/L/Regular'],
          color: 'content.default.level-5',
          mb: 16,
        },
        button: css.raw(button.raw({ display: 'flex', mode: 'light', size: 'medium' }), {
          tablet: button.raw({ display: 'inlineFlex', mode: 'light', size: 'large' }),
        }),
      },
      bottomSheet: {
        dialog: {
          left: 0,
          right: 0,
          bottom: 0,
          roundedTop: 16,
          transform: 'translateY(calc(100% - 100% * var(--progress)))',
        },
        topBar: {
          height: 20,
        },
        contents: {
          p: 16,
          _last: {
            pb: 40,
          },
        },
        bottom: { p: 16 },
        title: {
          textStyle: 'Title/M',
          color: 'content.default.level-5',
          mb: 16,
        },
        description: {
          textStyle: 'Text/S/Regular',
          color: 'content.default.level-5',
          mb: 24,
        },
        button: button.raw({ display: 'flex', mode: 'light', size: 'large' }),
      },
    },
  },
});
